import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Progress } from './ui/progress';
import { Building, Users, Layers, Settings as SettingsIcon, FileText, Save, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { toast } from 'sonner';
import { RecordContextHeader } from './RecordContextHeader';
import { NextStepPrompt } from './NextStepPrompt';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const getToken = () => localStorage.getItem('token');

const api = async (endpoint, options = {}) => {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...options.headers
  };
  const response = await fetch(`${API_URL}${endpoint}`, { ...options, headers });
  if (!response.ok) throw new Error(await response.text());
  return response.json();
};

const STEPS = [
  { id: 1, name: 'Facility Info', icon: Building },
  { id: 2, name: 'Building Details', icon: Layers },
  { id: 3, name: 'Room Counts', icon: Users },
  { id: 4, name: 'Operations', icon: SettingsIcon },
  { id: 5, name: 'Notes', icon: FileText }
];

export function MultiStepWalkthroughForm({ walkthroughId, leadId, onSave, onCancel }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [leadData, setLeadData] = useState(null);
  const [showNextSteps, setShowNextSteps] = useState(false);
  const [savedWalkthroughId, setSavedWalkthroughId] = useState(null);
  const [formData, setFormData] = useState({
    // Core
    lead_id: leadId || '',
    facility_name: '',
    service_address: '',
    
    // Site Metrics
    square_footage: '',
    building_type: 'office',
    floors_count: 1,
    multi_floor_building: false,
    elevator_access: false,
    
    // Access & Operations
    after_hours_access_issues: false,
    preferred_cleaning_window: 'evening',
    frequency: 'weekly',
    walkthrough_date: '',
    walkthrough_by: '',
    
    // Room Counts
    restrooms_count: 0,
    breakrooms_count: 0,
    offices_count: 0,
    conference_rooms_count: 0,
    exam_or_specialty_rooms_count: 0,
    warehouse_or_shop_area: false,
    lobby_or_common_area: true,
    
    // Condition & Flooring
    floor_types: [],
    condition: 'average',
    trash_volume: 'moderate',
    access_difficulty: 'easy',
    
    // Supplies
    supplies_included: true,
    consumables_provided_by: 'contractor',
    day_porter_needed: false,
    day_porter_hours_per_day: '',
    day_porter_days_per_week: '',
    
    // Notes
    special_instructions: '',
    site_notes: ''
  });

  useEffect(() => {
    if (walkthroughId) {
      loadWalkthrough();
    }
    if (leadId) {
      loadLead();
    }
  }, [walkthroughId, leadId]);

  const loadLead = async () => {
    try {
      const data = await api(`/api/leads/${leadId}`);
      setLeadData(data);
    } catch (error) {
      console.error('Failed to load lead:', error);
    }
  };

  const loadWalkthrough = async () => {
    try {
      const data = await api(`/api/walkthroughs/${walkthroughId}/zones-with-tasks`);
      // Extract only walkthrough fields, excluding zones
      const { zones, ...walkthroughData } = data;
      setFormData({
        ...walkthroughData,
        floor_types: walkthroughData.floor_types || [],
        lead_id: walkthroughData.lead_id || leadId
      });
    } catch (error) {
      toast.error('Failed to load walkthrough');
      console.error(error);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFloorTypeToggle = (type) => {
    const current = formData.floor_types || [];
    if (current.includes(type)) {
      handleChange('floor_types', current.filter(t => t !== type));
    } else {
      handleChange('floor_types', [...current, type]);
    }
  };

  const handleNext = () => {
    if (currentStep < STEPS.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSave = async () => {
    if (!formData.lead_id) {
      toast.error('Lead ID is required');
      return;
    }

    setSaving(true);
    try {
      // Exclude zones and other non-walkthrough fields
      const { zones, tasks, ...walkthroughFields } = formData;
      
      const dataToSave = {
        ...walkthroughFields,
        square_footage: parseInt(walkthroughFields.square_footage) || null,
        floors_count: parseInt(walkthroughFields.floors_count) || 1,
        restrooms_count: parseInt(walkthroughFields.restrooms_count) || 0,
        breakrooms_count: parseInt(walkthroughFields.breakrooms_count) || 0,
        offices_count: parseInt(walkthroughFields.offices_count) || 0,
        conference_rooms_count: parseInt(walkthroughFields.conference_rooms_count) || 0,
        exam_or_specialty_rooms_count: parseInt(walkthroughFields.exam_or_specialty_rooms_count) || 0,
        day_porter_hours_per_day: walkthroughFields.day_porter_hours_per_day ? parseInt(walkthroughFields.day_porter_hours_per_day) : null,
        day_porter_days_per_week: walkthroughFields.day_porter_days_per_week ? parseInt(walkthroughFields.day_porter_days_per_week) : null
      };

      let response;
      if (walkthroughId) {
        response = await api(`/api/walkthroughs/${walkthroughId}`, {
          method: 'PUT',
          body: JSON.stringify(dataToSave)
        });
        toast.success('Walkthrough updated successfully');
        setSavedWalkthroughId(walkthroughId);
      } else {
        response = await api('/api/walkthroughs', {
          method: 'POST',
          body: JSON.stringify(dataToSave)
        });
        toast.success('Walkthrough created successfully');
        setSavedWalkthroughId(response.id);
      }
      
      // Show next steps
      setShowNextSteps(true);
    } catch (error) {
      toast.error('Failed to save walkthrough');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const progress = (currentStep / STEPS.length) * 100;

  return (
    <div className="space-y-6">
      {/* Next Steps Prompt */}
      {showNextSteps && (
        <NextStepPrompt
          title="Walkthrough Saved!"
          message="What's your next step?"
          primaryAction="Manage Zones & Tasks"
          secondaryAction="Close"
          onPrimaryClick={() => {
            setShowNextSteps(false);
            if (onSave) onSave(savedWalkthroughId, 'manage-zones');
          }}
          onSecondaryClick={() => {
            setShowNextSteps(false);
            if (onSave) onSave(savedWalkthroughId);
          }}
        />
      )}

      {/* Context Header */}
      {leadData && (
        <RecordContextHeader
          label="Active Walkthrough"
          company={leadData.company_name}
          facility={formData.facility_name || 'New Walkthrough'}
          status={walkthroughId ? 'in_progress' : 'new'}
          additionalInfo={formData.square_footage ? `${formData.square_footage} sq ft` : ''}
        />
      )}

      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-[Space_Grotesk] font-semibold">
          {walkthroughId ? 'Edit Walkthrough' : 'New Walkthrough'}
        </h2>
        <Button variant="outline" onClick={onCancel} disabled={loading}>
          <X className="h-4 w-4 mr-2" />
          Cancel
        </Button>
      </div>

      {/* Progress Bar */}
      <div className="space-y-2">
        <div className="flex justify-between text-sm text-muted-foreground">
          <span>Step {currentStep} of {STEPS.length}</span>
          <span>{STEPS[currentStep - 1].name}</span>
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      {/* Step Indicators */}
      <div className="flex justify-between">
        {STEPS.map((step) => {
          const Icon = step.icon;
          const isActive = step.id === currentStep;
          const isComplete = step.id < currentStep;
          
          return (
            <div
              key={step.id}
              className={`flex flex-col items-center gap-2 ${isActive ? 'text-teal-600' : isComplete ? 'text-green-600' : 'text-gray-400'}`}
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isActive ? 'bg-teal-100 border-2 border-teal-600' : isComplete ? 'bg-green-100' : 'bg-gray-100'}`}>
                <Icon className="h-5 w-5" />
              </div>
              <span className="text-xs font-medium hidden md:block">{step.name}</span>
            </div>
          );
        })}
      </div>

      {/* Step Content */}
      <Card>
        <CardContent className="pt-6">
          {/* Step 1: Facility Info */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <CardHeader className="px-0">
                <CardTitle>Facility Information</CardTitle>
                <CardDescription>Basic facility details and location</CardDescription>
              </CardHeader>
              
              <div>
                <Label htmlFor="facility_name">Facility Name</Label>
                <Input
                  id="facility_name"
                  value={formData.facility_name}
                  onChange={(e) => handleChange('facility_name', e.target.value)}
                  placeholder="Corporate Headquarters"
                  data-testid="facility-name-input"
                />
              </div>

              <div>
                <Label htmlFor="service_address">Service Address</Label>
                <Input
                  id="service_address"
                  value={formData.service_address}
                  onChange={(e) => handleChange('service_address', e.target.value)}
                  placeholder="123 Main St, Denver, CO 80202"
                  data-testid="service-address-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="square_footage">Square Footage</Label>
                  <Input
                    id="square_footage"
                    type="number"
                    value={formData.square_footage}
                    onChange={(e) => handleChange('square_footage', e.target.value)}
                    placeholder="10000"
                    data-testid="square-footage-input"
                  />
                </div>

                <div>
                  <Label htmlFor="building_type">Building Type</Label>
                  <select
                    id="building_type"
                    className="w-full h-10 px-3 rounded-md border border-gray-300"
                    value={formData.building_type}
                    onChange={(e) => handleChange('building_type', e.target.value)}
                    data-testid="building-type-select"
                  >
                    <option value="office">Office</option>
                    <option value="medical">Medical</option>
                    <option value="retail">Retail</option>
                    <option value="industrial">Industrial</option>
                    <option value="mixed">Mixed Use</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Building Details */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <CardHeader className="px-0">
                <CardTitle>Building Details</CardTitle>
                <CardDescription>Physical characteristics and access</CardDescription>
              </CardHeader>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="floors_count">Number of Floors</Label>
                  <Input
                    id="floors_count"
                    type="number"
                    value={formData.floors_count}
                    onChange={(e) => handleChange('floors_count', e.target.value)}
                    min="1"
                    data-testid="floors-count-input"
                  />
                </div>

                <div className="flex items-center space-x-2 pt-8">
                  <Checkbox
                    id="multi_floor"
                    checked={formData.multi_floor_building}
                    onCheckedChange={(checked) => handleChange('multi_floor_building', checked)}
                    data-testid="multi-floor-checkbox"
                  />
                  <Label htmlFor="multi_floor">Multi-floor building</Label>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="elevator_access"
                  checked={formData.elevator_access}
                  onCheckedChange={(checked) => handleChange('elevator_access', checked)}
                  data-testid="elevator-checkbox"
                />
                <Label htmlFor="elevator_access">Elevator access available</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="lobby_area"
                  checked={formData.lobby_or_common_area}
                  onCheckedChange={(checked) => handleChange('lobby_or_common_area', checked)}
                  data-testid="lobby-checkbox"
                />
                <Label htmlFor="lobby_area">Has lobby or common area</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="warehouse_area"
                  checked={formData.warehouse_or_shop_area}
                  onCheckedChange={(checked) => handleChange('warehouse_or_shop_area', checked)}
                  data-testid="warehouse-checkbox"
                />
                <Label htmlFor="warehouse_area">Has warehouse or shop area</Label>
              </div>

              <div>
                <Label>Floor Types (select all that apply)</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {['carpet', 'tile', 'vinyl', 'concrete', 'hardwood'].map(type => (
                    <div key={type} className="flex items-center space-x-2">
                      <Checkbox
                        id={`floor_${type}`}
                        checked={(formData.floor_types || []).includes(type)}
                        onCheckedChange={() => handleFloorTypeToggle(type)}
                        data-testid={`floor-type-${type}`}
                      />
                      <Label htmlFor={`floor_${type}`} className="capitalize">{type}</Label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="condition">Condition</Label>
                  <select
                    id="condition"
                    className="w-full h-10 px-3 rounded-md border border-gray-300"
                    value={formData.condition}
                    onChange={(e) => handleChange('condition', e.target.value)}
                    data-testid="condition-select"
                  >
                    <option value="poor">Poor</option>
                    <option value="below_average">Below Average</option>
                    <option value="average">Average</option>
                    <option value="above_average">Above Average</option>
                    <option value="excellent">Excellent</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="trash_volume">Trash Volume</Label>
                  <select
                    id="trash_volume"
                    className="w-full h-10 px-3 rounded-md border border-gray-300"
                    value={formData.trash_volume}
                    onChange={(e) => handleChange('trash_volume', e.target.value)}
                    data-testid="trash-volume-select"
                  >
                    <option value="light">Light</option>
                    <option value="moderate">Moderate</option>
                    <option value="heavy">Heavy</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="access_difficulty">Access Difficulty</Label>
                  <select
                    id="access_difficulty"
                    className="w-full h-10 px-3 rounded-md border border-gray-300"
                    value={formData.access_difficulty}
                    onChange={(e) => handleChange('access_difficulty', e.target.value)}
                    data-testid="access-difficulty-select"
                  >
                    <option value="easy">Easy</option>
                    <option value="moderate">Moderate</option>
                    <option value="difficult">Difficult</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Room Counts */}
          {currentStep === 3 && (
            <div className="space-y-4">
              <CardHeader className="px-0">
                <CardTitle>Room Counts</CardTitle>
                <CardDescription>Number of rooms and spaces by type</CardDescription>
              </CardHeader>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="restrooms">Restrooms</Label>
                  <Input
                    id="restrooms"
                    type="number"
                    value={formData.restrooms_count}
                    onChange={(e) => handleChange('restrooms_count', e.target.value)}
                    min="0"
                    data-testid="restrooms-input"
                  />
                </div>

                <div>
                  <Label htmlFor="breakrooms">Breakrooms</Label>
                  <Input
                    id="breakrooms"
                    type="number"
                    value={formData.breakrooms_count}
                    onChange={(e) => handleChange('breakrooms_count', e.target.value)}
                    min="0"
                    data-testid="breakrooms-input"
                  />
                </div>

                <div>
                  <Label htmlFor="offices">Offices</Label>
                  <Input
                    id="offices"
                    type="number"
                    value={formData.offices_count}
                    onChange={(e) => handleChange('offices_count', e.target.value)}
                    min="0"
                    data-testid="offices-input"
                  />
                </div>

                <div>
                  <Label htmlFor="conference_rooms">Conference Rooms</Label>
                  <Input
                    id="conference_rooms"
                    type="number"
                    value={formData.conference_rooms_count}
                    onChange={(e) => handleChange('conference_rooms_count', e.target.value)}
                    min="0"
                    data-testid="conference-rooms-input"
                  />
                </div>

                <div>
                  <Label htmlFor="specialty_rooms">Exam / Specialty Rooms</Label>
                  <Input
                    id="specialty_rooms"
                    type="number"
                    value={formData.exam_or_specialty_rooms_count}
                    onChange={(e) => handleChange('exam_or_specialty_rooms_count', e.target.value)}
                    min="0"
                    data-testid="specialty-rooms-input"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Operations */}
          {currentStep === 4 && (
            <div className="space-y-4">
              <CardHeader className="px-0">
                <CardTitle>Operational Factors</CardTitle>
                <CardDescription>Service schedule and supply details</CardDescription>
              </CardHeader>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="cleaning_window">Preferred Cleaning Window</Label>
                  <select
                    id="cleaning_window"
                    className="w-full h-10 px-3 rounded-md border border-gray-300"
                    value={formData.preferred_cleaning_window}
                    onChange={(e) => handleChange('preferred_cleaning_window', e.target.value)}
                    data-testid="cleaning-window-select"
                  >
                    <option value="evening">Evening (5PM-9PM)</option>
                    <option value="night">Night (9PM-1AM)</option>
                    <option value="early_morning">Early Morning (4AM-8AM)</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="frequency">Service Frequency</Label>
                  <select
                    id="frequency"
                    className="w-full h-10 px-3 rounded-md border border-gray-300"
                    value={formData.frequency}
                    onChange={(e) => handleChange('frequency', e.target.value)}
                    data-testid="frequency-select"
                  >
                    <option value="nightly">Nightly (5-7x/week)</option>
                    <option value="weekly">Weekly (1x/week)</option>
                    <option value="bi_weekly">Bi-weekly (2x/month)</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="consumables">Consumables Provided By</Label>
                  <select
                    id="consumables"
                    className="w-full h-10 px-3 rounded-md border border-gray-300"
                    value={formData.consumables_provided_by}
                    onChange={(e) => handleChange('consumables_provided_by', e.target.value)}
                    data-testid="consumables-select"
                  >
                    <option value="client">Client</option>
                    <option value="contractor">Contractor</option>
                    <option value="mixed">Mixed</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="supplies_included"
                  checked={formData.supplies_included}
                  onCheckedChange={(checked) => handleChange('supplies_included', checked)}
                  data-testid="supplies-checkbox"
                />
                <Label htmlFor="supplies_included">Cleaning supplies included in service</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="after_hours_issues"
                  checked={formData.after_hours_access_issues}
                  onCheckedChange={(checked) => handleChange('after_hours_access_issues', checked)}
                  data-testid="after-hours-checkbox"
                />
                <Label htmlFor="after_hours_issues">After-hours access issues</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="day_porter"
                  checked={formData.day_porter_needed}
                  onCheckedChange={(checked) => handleChange('day_porter_needed', checked)}
                  data-testid="day-porter-checkbox"
                />
                <Label htmlFor="day_porter">Day porter needed</Label>
              </div>

              {formData.day_porter_needed && (
                <div className="grid grid-cols-2 gap-4 ml-6">
                  <div>
                    <Label htmlFor="porter_hours">Hours per Day</Label>
                    <Input
                      id="porter_hours"
                      type="number"
                      value={formData.day_porter_hours_per_day}
                      onChange={(e) => handleChange('day_porter_hours_per_day', e.target.value)}
                      min="1"
                      max="24"
                      data-testid="porter-hours-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="porter_days">Days per Week</Label>
                    <Input
                      id="porter_days"
                      type="number"
                      value={formData.day_porter_days_per_week}
                      onChange={(e) => handleChange('day_porter_days_per_week', e.target.value)}
                      min="1"
                      max="7"
                      data-testid="porter-days-input"
                    />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="walkthrough_date">Walkthrough Date</Label>
                  <Input
                    id="walkthrough_date"
                    type="date"
                    value={formData.walkthrough_date}
                    onChange={(e) => handleChange('walkthrough_date', e.target.value)}
                    data-testid="walkthrough-date-input"
                  />
                </div>

                <div>
                  <Label htmlFor="walkthrough_by">Conducted By</Label>
                  <Input
                    id="walkthrough_by"
                    value={formData.walkthrough_by}
                    onChange={(e) => handleChange('walkthrough_by', e.target.value)}
                    placeholder="Sales Rep Name"
                    data-testid="walkthrough-by-input"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Notes */}
          {currentStep === 5 && (
            <div className="space-y-4">
              <CardHeader className="px-0">
                <CardTitle>Notes & Special Instructions</CardTitle>
                <CardDescription>Additional details and requirements</CardDescription>
              </CardHeader>

              <div>
                <Label htmlFor="special_instructions">Special Instructions</Label>
                <Textarea
                  id="special_instructions"
                  value={formData.special_instructions}
                  onChange={(e) => handleChange('special_instructions', e.target.value)}
                  rows={5}
                  placeholder="Security requirements, access codes, special handling, etc."
                  data-testid="special-instructions-input"
                />
              </div>

              <div>
                <Label htmlFor="site_notes">Site Notes</Label>
                <Textarea
                  id="site_notes"
                  value={formData.site_notes}
                  onChange={(e) => handleChange('site_notes', e.target.value)}
                  rows={5}
                  placeholder="Internal notes about the site..."
                  data-testid="site-notes-input"
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={handleBack}
          disabled={currentStep === 1 || loading}
        >
          <ChevronLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        <div className="flex gap-2">
          {currentStep < STEPS.length ? (
            <Button onClick={handleNext}>
              Next
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button onClick={handleSave} disabled={loading} data-testid="save-walkthrough-button">
              <Save className="h-4 w-4 mr-2" />
              {loading ? 'Saving...' : 'Save Walkthrough'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
